/**
 * Last-conversation-activity stamp for the idle blank slate (BIZY-683).
 *
 * "Activity" means the user actually conversed with Bizy: they sent a message,
 * a reply finished streaming, or they deliberately opened a conversation from
 * history. Those moments call `markBizyActivity()`. Passive mechanics — mount
 * hydration, timeline merges, cross-tab refetches, coordinator auto-loads —
 * never touch the stamp, so they can't mask a stale conversation.
 *
 * Stored in localStorage so it survives reloads (the load-Bizy-by-URL trigger
 * depends on that) and is shared across tabs (activity in one tab means the
 * user isn't idle in another). A missing stamp reads as stale: when a
 * conversation exists but we have no evidence of recent activity, the
 * uncluttered fresh start is the right default.
 */

export const BIZY_LAST_ACTIVITY_STORAGE_KEY = 'bizy-last-activity-at';

/** Conversation age at which the chat presents the blank slate. */
export const BIZY_IDLE_BLANK_SLATE_MS = 5 * 60 * 1000;

// QA/dev override: `localStorage.setItem('bizy-idle-blank-slate-ms', '5000')`
// makes the blank slate fire once the conversation is 5s old instead of 5 min.
export const BIZY_IDLE_BLANK_SLATE_OVERRIDE_KEY = 'bizy-idle-blank-slate-ms';

export function getIdleBlankSlateMs(): number {
	try {
		const overrideMs = Number(localStorage.getItem(BIZY_IDLE_BLANK_SLATE_OVERRIDE_KEY));
		if (Number.isFinite(overrideMs) && overrideMs > 0) return overrideMs;
	} catch {
		// localStorage unavailable
	}
	return BIZY_IDLE_BLANK_SLATE_MS;
}

export function markBizyActivity(): void {
	try {
		localStorage.setItem(BIZY_LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
	} catch {
		// localStorage unavailable — staleness falls back to "stale", which
		// only means the user sees a fresh start more often.
	}
}

/** Epoch ms of the last conversation activity, or null when never recorded. */
export function getLastBizyActivityMs(): number | null {
	try {
		const stampMs = Number(localStorage.getItem(BIZY_LAST_ACTIVITY_STORAGE_KEY));
		return Number.isFinite(stampMs) && stampMs > 0 ? stampMs : null;
	} catch {
		return null;
	}
}

/** True when the last activity is older than the idle threshold (or unknown). */
export function isBizyConversationStale(): boolean {
	const lastActivityMs = getLastBizyActivityMs();
	if (lastActivityMs === null) return true;
	return Date.now() - lastActivityMs >= getIdleBlankSlateMs();
}
